/**
 * Список аудиовходов для формы записи.
 *
 * Браузеры отдают сырой enumerateDevices() с артефактами, которые нельзя показывать как есть:
 * - Chromium добавляет виртуальные алиасы `default` (все ОС) и `communications` (Windows) —
 *   это не отдельные устройства, а «то, что сейчас выбрано в системе»; их label локализуется
 *   браузером («Default - X»), поэтому опознавать их можно только по deviceId;
 * - до успешного getUserMedia список урезан до одной пустой записи на тип устройства
 *   (deviceId/label/groupId = ""), её показывать нечем и незачем;
 * - у одного физического устройства может быть несколько записей.
 *
 * Firefox и Safari виртуальных алиасов не создают — «следование за системой» там существует
 * только как отсутствие deviceId в constraints, что у нас и есть опция `systemDefault`.
 *
 * Модуль общий для всех мест записи (таймлайн, надиктовка промпта): выбор входа один
 * на браузер, поэтому и логика, и преф живут здесь, а не рядом с одной из форм.
 */

/** Виртуальные записи Chromium: не устройства, а роли. */
const DEFAULT_DEVICE_ID = "default"
const COMMUNICATIONS_DEVICE_ID = "communications"

/** Псевдо-значение селектора: захват звука вкладки/экрана, а не микрофон. */
export const SYSTEM_AUDIO_INPUT_VALUE = "__system__"

export type AudioInputDevice = {
  deviceId: string
  groupId: string
  label: string
}

export type AudioInputList = {
  /** Все реальные устройства (алиасы исключены). */
  devices: AudioInputDevice[]
  /** deviceId устройства, на которое указывает алиас `default`; null — алиаса нет. */
  systemDefaultDeviceId: string | null
}

/** Сохранённый выбор: deviceId ненадёжен (Safari рандомизирует его на каждую загрузку). */
export type SavedAudioInput = {
  deviceId: string
  groupId: string
  label: string
}

/**
 * Последний выбранный вход записи. Храним тройку {deviceId, groupId, label}, а не голый
 * deviceId: он живёт только до очистки данных сайта, а Safari вообще рандомизирует его на
 * каждую загрузку — узнать устройство помогают groupId и имя (см. matchSavedInput).
 * Отсутствие префа = вход по умолчанию (следуем за системой).
 */
export type AudioInputPref = { kind: "system" } | { kind: "device"; deviceId: string; groupId: string; label: string }

/** Ключ из времён, когда выбор входа жил только в таймлайне: переименование стёрло бы память. */
const AUDIO_INPUT_STORAGE_KEY = "timeline-audio-input"

function parseAudioInputPref(raw: string | null): AudioInputPref | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return null
    const pref = parsed as Partial<Record<string, unknown>>
    if (pref.kind === "system") return { kind: "system" }
    if (pref.kind === "device" && typeof pref.deviceId === "string") {
      return {
        kind: "device",
        deviceId: pref.deviceId,
        groupId: typeof pref.groupId === "string" ? pref.groupId : "",
        label: typeof pref.label === "string" ? pref.label : "",
      }
    }
    return null
  } catch {
    return null
  }
}

function readStoredAudioInputPref(): AudioInputPref | null {
  if (typeof window === "undefined") return null
  return parseAudioInputPref(window.localStorage.getItem(AUDIO_INPUT_STORAGE_KEY))
}

export function audioInputPrefsEqual(a: AudioInputPref | null, b: AudioInputPref | null): boolean {
  if (a === b) return true
  if (!a || !b || a.kind !== b.kind) return false
  if (a.kind === "device" && b.kind === "device") {
    return a.deviceId === b.deviceId && a.groupId === b.groupId && a.label === b.label
  }
  return true
}

/**
 * Живой снимок префа: пишущий инстанс и остальные на странице видят одно и то же.
 * localStorage.storage не стреляет в той же вкладке — поэтому ещё и свои слушатели.
 */
const audioInputPrefListeners = new Set<() => void>()
let audioInputPrefSnapshot: AudioInputPref | null | undefined

function emitAudioInputPref(): void {
  for (const listener of audioInputPrefListeners) listener()
}

export function subscribeAudioInputPref(listener: () => void): () => void {
  audioInputPrefListeners.add(listener)
  return () => {
    audioInputPrefListeners.delete(listener)
  }
}

export function getAudioInputPrefSnapshot(): AudioInputPref | null {
  if (audioInputPrefSnapshot === undefined) audioInputPrefSnapshot = readStoredAudioInputPref()
  return audioInputPrefSnapshot
}

export function readAudioInputPref(): AudioInputPref | null {
  return getAudioInputPrefSnapshot()
}

export function writeAudioInputPref(pref: AudioInputPref | null): void {
  if (audioInputPrefSnapshot !== undefined && audioInputPrefsEqual(audioInputPrefSnapshot, pref)) return
  audioInputPrefSnapshot = pref
  if (typeof window !== "undefined") {
    if (pref) window.localStorage.setItem(AUDIO_INPUT_STORAGE_KEY, JSON.stringify(pref))
    else window.localStorage.removeItem(AUDIO_INPUT_STORAGE_KEY)
  }
  emitAudioInputPref()
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== AUDIO_INPUT_STORAGE_KEY) return
    const next = parseAudioInputPref(event.newValue)
    if (audioInputPrefSnapshot !== undefined && audioInputPrefsEqual(audioInputPrefSnapshot, next)) return
    audioInputPrefSnapshot = next
    emitAudioInputPref()
  })
}

/**
 * Значение селектора из префа: надиктовка не умеет «звук вкладки», поэтому system
 * там выглядит как вход по умолчанию. Повторный клик по уже выбранному пункту
 * не должен писать nextAudioInputPref("") и стирать таймлайнный system-преф.
 */
export function resolveSelectorValue(
  pref: AudioInputPref | null,
  devices: AudioInputDevice[],
  allowSystemAudio: boolean,
): string {
  if (!pref) return ""
  if (pref.kind === "system") return allowSystemAudio ? SYSTEM_AUDIO_INPUT_VALUE : ""
  return matchSavedInput(pref, devices)?.deviceId ?? pref.deviceId
}

function isVirtual(deviceId: string): boolean {
  return deviceId === DEFAULT_DEVICE_ID || deviceId === COMMUNICATIONS_DEVICE_ID
}

/** Разделитель в label алиаса: Chromium форматирует его как «<Default> - <имя устройства>». */
const ALIAS_LABEL_SEPARATOR = " - "

/**
 * Находит реальное устройство, на которое указывает алиас `default`.
 * Основной ключ — groupId (Chromium копирует его с физического устройства);
 * запасной — хвост label. Хвост сверяем вместе с разделителем: иначе «Default - Внешний
 * Микрофон» ошибочно совпал бы с устройством «Микрофон». Локализуется только слово
 * «Default», сам разделитель — литерал, поэтому на него опираться можно.
 */
function resolveDefault(alias: AudioInputDevice, devices: AudioInputDevice[]): AudioInputDevice | null {
  const byGroup = alias.groupId ? devices.find((d) => d.groupId === alias.groupId) : undefined
  if (byGroup) return byGroup
  if (!alias.label) return null
  return (
    devices.find((d) => d.label && alias.label.endsWith(`${ALIAS_LABEL_SEPARATOR}${d.label}`)) ??
    devices.find((d) => d.label && alias.label === d.label) ??
    null
  )
}

/** Приводит сырой enumerateDevices() к списку для селектора. */
export function buildAudioInputs(all: MediaDeviceInfo[]): AudioInputList {
  const inputs = all
    .filter((d) => d.kind === "audioinput" && d.deviceId)
    .map((d) => ({ deviceId: d.deviceId, groupId: d.groupId, label: d.label }))

  const physical: AudioInputDevice[] = []
  const seen = new Set<string>()
  for (const device of inputs) {
    if (isVirtual(device.deviceId) || seen.has(device.deviceId)) continue
    seen.add(device.deviceId)
    physical.push(device)
  }

  const alias = inputs.find((d) => d.deviceId === DEFAULT_DEVICE_ID)
  const systemDefault = alias ? resolveDefault(alias, physical) : null

  // Устройство, на которое указывает алиас, из списка НЕ убираем: иначе его нельзя
  // выбрать явно (закрепить), а выбранное устройство пропадало бы из списка в момент,
  // когда оно становится системным по умолчанию.
  return { devices: physical, systemDefaultDeviceId: systemDefault?.deviceId ?? null }
}

/**
 * Ищет сохранённое устройство в текущем списке: точный id → то же физическое
 * устройство (groupId) → то же имя. Не нашли — вызывающий откатывается на системный вход.
 */
export function matchSavedInput(saved: SavedAudioInput | null, devices: AudioInputDevice[]): AudioInputDevice | null {
  if (!saved) return null
  return (
    devices.find((d) => saved.deviceId && d.deviceId === saved.deviceId) ??
    devices.find((d) => saved.groupId && d.groupId === saved.groupId) ??
    devices.find((d) => saved.label && d.label === saved.label) ??
    null
  )
}

export type AudioInputSelection =
  | { kind: "default" }
  | { kind: "system" }
  /** exact=false — устройство ещё не подтверждено списком (прав нет): ограничение мягкое. */
  | { kind: "device"; deviceId: string; exact: boolean }

/**
 * Разбирает значение селектора в источник записи.
 *
 * `exact` ставим только для устройства, которое реально есть в текущем списке: явный
 * выбор пользователя браузер обязан выполнить (у Chrome свой рейтинг микрофонов, и
 * мягкое ограничение он вправе перебить). Для непроверенного сохранённого id — мягкое:
 * он мог протухнуть (в Safari deviceId новый на каждую загрузку), и `exact` дал бы
 * OverconstrainedError на ровном месте.
 */
export function resolveAudioInputSelection(value: string, devices: AudioInputDevice[]): AudioInputSelection {
  if (value === SYSTEM_AUDIO_INPUT_VALUE) return { kind: "system" }
  if (!value) return { kind: "default" }
  return { kind: "device", deviceId: value, exact: devices.some((d) => d.deviceId === value) }
}

/**
 * Преф после выбора в селекторе. Значение, которого ещё нет в списке (права не выданы,
 * устройства не раскрыты), не должно стирать память: возвращаем текущий преф как есть —
 * иначе клик по собственному пункту «свой микрофон» забывал бы его.
 */
export function nextAudioInputPref(
  value: string,
  devices: AudioInputDevice[],
  current: AudioInputPref | null,
): AudioInputPref | null {
  if (value === SYSTEM_AUDIO_INPUT_VALUE) return { kind: "system" }
  if (!value) return null
  const device = devices.find((d) => d.deviceId === value)
  if (device) return { kind: "device", deviceId: device.deviceId, groupId: device.groupId, label: device.label }
  return current?.kind === "device" && current.deviceId === value ? current : null
}
/** Пункт селектора входа: `value` совпадает со значением, которое понимает resolveAudioInputSelection. */
export type AudioInputOption = {
  value: string
  label: string
}

/**
 * Разрешает алиас `default` в физическое устройство: getSettings().deviceId в Chrome
 * возвращает именно алиас, а сравнивать и подписывать им нечего — это роль, не устройство.
 * Всё остальное (включая null «неизвестно») отдаём как есть.
 */
export function resolvePhysicalDeviceId(deviceId: string | null, systemDefaultDeviceId: string | null): string | null {
  return deviceId === DEFAULT_DEVICE_ID ? systemDefaultDeviceId : deviceId
}

/**
 * Список пунктов селектора: вход по умолчанию (значение "") плюс все устройства.
 * Особые пункты вроде «звук вкладки» вызывающий добавляет сам — они не микрофоны.
 *
 * pendingLabel — имя сохранённого устройства, которого ещё нет в списке (права не выданы,
 * браузер устройства не раскрыл). Без такого пункта селектор показывал бы «по умолчанию»,
 * хотя первая запись пойдёт с запомненного микрофона.
 */
export function buildAudioInputOptions(params: {
  devices: AudioInputDevice[]
  /** Текущее значение селектора — под него подставляется pendingLabel. */
  value: string
  pendingLabel: string | null
  /** Устройство, которым подписывается пункт «по умолчанию» (см. resolveDefaultInputDeviceId). */
  defaultDeviceId: string | null
  labels: {
    defaultInput: string
    defaultInputNamed: (name: string) => string
    microphoneN: (n: number) => string
  }
}): AudioInputOption[] {
  const { devices, value, pendingLabel, defaultDeviceId, labels } = params
  const pending =
    pendingLabel != null && value && !devices.some((d) => d.deviceId === value)
      ? { value, label: pendingLabel || labels.microphoneN(1) }
      : null
  const defaultLabel = devices.find((d) => d.deviceId === defaultDeviceId)?.label
  return [
    { value: "", label: defaultLabel ? labels.defaultInputNamed(defaultLabel) : labels.defaultInput },
    ...(pending ? [pending] : []),
    ...devices.map((d, i) => ({ value: d.deviceId, label: d.label || labels.microphoneN(i + 1) })),
  ]
}
